import { useState, useEffect, useMemo } from "react";

import Form from "V2.0/components/Form";
import Joi from "joi-browser";
import { message } from "antd";
import httpService from "V2.0/services/httpService";
import {
  addContactAttributesIfPresent,
  fieldTemplates,
  fieldSchema,
  fieldValues,
} from "../../common/contactFields";
import { Button } from "V2.0/common/button";
import WhatsappTemplateAttributesAdd from "./WhatsappTemplateAttributesAdd";
import { processTemplateForParameters } from "./utils";
import { PlusIcon } from "@heroicons/react/24/outline";
import {
  toNumberField,
  triggerTypeField,
} from "V2.0/HomePages/Campaigns/common/fields";

export default function MetaWhatsApp(props) {
  const {
    addUserToCampaign,
    onClose,
    projectId,
    assistantId,
    fromNumber,
    ignorePreviousCampaigns,
  } = props;
  const [loading, setLoading] = useState(false);
  const [messageTemplates, setMessageTemplates] = useState([]);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [showAttributesAdd, setShowAttributesAdd] = useState(false);
  const [attributes, setAttributes] = useState({});

  const [data, setData] = useState({
    template: JSON.stringify({
      templateName: "",
      templateLanguage: "",
    }),
  });

  const [inputField] = useState({
    ...fieldValues,
    toNumber: "",
    fromNumber,
    triggerType: "",
    campaignType: "whatsapp",
    template: JSON.stringify({
      templateName: "",
      templateLanguage: "",
    }),
    assistantId,
    ignorePreviousCampaigns,
  });

  const updatedInputField = useMemo(() => {
    return {
      ...inputField,
      assistantId,
      fromNumber,
      ignorePreviousCampaigns,
    };
  }, [assistantId, fromNumber, inputField, ignorePreviousCampaigns]);

  const parameters = useMemo(() => {
    if (!Boolean(activeTemplate)) {
      return [];
    }

    return processTemplateForParameters(activeTemplate);
  }, [activeTemplate]);

  const [errorMessage] = useState({});

  const fetchWhatsappMessageTemplates = async () => {
    try {
      const response = await httpService.get(
        `/whatsapp/manage/whitelabelGetTemplates/${projectId}`
      );
      const data = response.data;
      setMessageTemplates(data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchWhatsappMessageTemplates();
  }, []);

  useEffect(() => {
    const inputFieldTemplate = JSON.parse(data?.template);

    if (Boolean(inputFieldTemplate?.templateName)) {
      const templateName = inputFieldTemplate.templateName;

      const messageTemplate = messageTemplates.find((_messageTemplate) => {
        return _messageTemplate.name === templateName;
      });

      setActiveTemplate(messageTemplate);
    } else {
      setActiveTemplate(null);
    }
  }, [data, messageTemplates]);

  const template = [
    ...fieldTemplates,
    toNumberField,
    {
      name: "template",
      label: "Message Template",
      description: "Select one of the pre approved templates",
      type: "select",
      options: [
        { id: 0, value: JSON.stringify({}), label: "(No template selected)" },
      ].concat(
        messageTemplates.map((messageTemplate, index) => ({
          id: index,
          value: JSON.stringify({
            templateName: messageTemplate.name,
            templateLanguage: messageTemplate.language,
          }),
          label: messageTemplate.name,
        }))
      ),
      info:
        Boolean(activeTemplate) &&
        (Boolean(parameters.length) ? (
          <Button
            plain
            disabled={!Boolean(activeTemplate)}
            onClick={() => setShowAttributesAdd(true)}
          >
            <PlusIcon /> Attributes
          </Button>
        ) : (
          <Button
            plain
            onClick={() => setShowAttributesAdd(true)}
          >
            View Message
          </Button>
        )),
    },
    triggerTypeField,
  ];

  const schema = {
    ...fieldSchema,
    toNumber: Joi.string()
      .regex(/^\+?[0-9]+$/)
      .required()
      .label("To Number"),
    template: Joi.object({
      templateName: Joi.string().required(),
      templateLanguage: Joi.string().required(),
    })
      .required()
      .label("Message Template"),
    fromNumber: Joi.string()
      .regex(/^\+?[0-9]+$/)
      .required()
      .label("From Number"),
    triggerType: Joi.string().required().label("Trigger Type"),
    campaignType: Joi.string().required().label("campaignType"),
    assistantId: Joi.string().required(),
    ignorePreviousCampaigns: Joi.boolean(),
  };

  function onAddUserToCampaign(data) {
    const { template, firstName, lastName, email, ...rest } = data;
    const { templateName, templateLanguage } = JSON.parse(template);

    const contactAttributes = addContactAttributesIfPresent(data);

    const updatedData = {
      ...rest,
      templateName,
      templateLanguage,
      attributes: {
        ...attributes,
        ...(contactAttributes?.attributes ?? {}),
      },
    };

    addUserToCampaign(
      updatedData,
      (response) => {
        if (response.status === 200) {
          message.success("Success");
          setLoading(false);
          onClose();
        }
      },
      (error) => {
        message.error(
          error.response.data?.message ||
            error.response.data?.msg ||
            error.response.data?.error ||
            "Failed to add contact"
        );
        setLoading(false);
        onClose();
        console.error(error);
      }
    );
  }

  return (
    <>
      <Form
        key={JSON.stringify(updatedInputField)}
        errorMessage={errorMessage}
        inputField={updatedInputField}
        template={template}
        schema={schema}
        buttonInfo={{
          style: "",
          buttons: "2",
          primaryLabel: "Start",
          secondaryLabel: "Close",
        }}
        onSubmit={onAddUserToCampaign}
        loading={loading}
        setLoading={setLoading}
        handleSecondary={onClose}
        getValue={(data) => setData(data)}
      />
      {showAttributesAdd && (
        <WhatsappTemplateAttributesAdd
          parametersInitialValues={attributes}
          template={activeTemplate}
          onUpdateParameters={(data) => setAttributes(data)}
          onClose={() => setShowAttributesAdd(false)}
        />
      )}
    </>
  );
}
